import { useEffect, useState } from "react";
import api, { euro, apiError, fmtDate, pctIt } from "@/lib/api";
import { toast } from "sonner";
import { Lock, Clock, Landmark, CheckCircle2 } from "lucide-react";

const LATI = [["venditore", "Venditore"], ["acquirente", "Acquirente"]];
const STATI = [["prevista", "Prevista"], ["probabile", "Probabile (proposta)"], ["sicura", "Sicura (preliminare)"], ["incassata", "Incassata"]];
const COLS = [["richiesto", "Sul prezzo richiesto"], ["proposta", "Sulla proposta"], ["venduto", "Sul venduto"]];
const RUOLI = { acquisitore: "Agente acquisitore", venditore: "Agente venditore", direttore: "Direttore", segnalatore: "Segnalatore", agente: "Agente" };
const inp = "w-full px-3 py-2 rounded-lg border border-[var(--border)] text-sm bg-white disabled:bg-[#F7F7F8]";

function formDa(inc) {
  const f = {};
  LATI.forEach(([l]) => {
    f[`provv_${l}_modo`] = inc[l].modo || (l === "venditore" ? "pct" : "");
    f[`provv_${l}_pct`] = inc[l].pct ?? "";
    f[`provv_${l}_eur`] = inc[l].eur ?? "";
  });
  f.provv_minimo_eur = inc.minimo_eur ?? "";
  return f;
}

function LatoInput({ l, label, f, setF, std, disabled, err }) {
  const modo = f[`provv_${l}_modo`];
  const k = modo === "eur" ? `provv_${l}_eur` : `provv_${l}_pct`;
  return (
    <div data-testid={`provv-lato-${l}`}>
      <div className="text-[11px] font-bold uppercase tracking-wide text-[var(--muted)] mb-1.5">Provvigione {label}</div>
      <div className="flex gap-1 mb-2">
        {[["pct", "% sul prezzo"], ["eur", "Forfait €"], ...(l === "acquirente" ? [["", "Nessuna"]] : [])].map(([v, t]) => (
          <button key={v || "no"} type="button" disabled={disabled} data-testid={`provv-${l}-modo-${v || "nessuna"}`} onClick={() => setF({ ...f, [`provv_${l}_modo`]: v })}
            className={`px-2.5 py-1 rounded-lg text-xs font-medium border transition-colors disabled:opacity-60 ${modo === v ? "bg-[#111111] text-white border-transparent" : "bg-[#F7F7F8] border-[var(--border)]"}`}>{t}</button>
        ))}
      </div>
      {modo && (
        <div className="flex items-center gap-2">
          <input data-testid={`provv-${l}-valore`} type="number" step={modo === "eur" ? "100" : "0.1"} min="0" disabled={disabled} value={f[k]}
            onChange={(e) => setF({ ...f, [k]: e.target.value })} className={`${inp} ${err ? "border-red-400" : ""}`} />
          <span className="text-xs text-[var(--muted)] whitespace-nowrap">{modo === "eur" ? "€" : "%"} + IVA</span>
        </div>
      )}
      {err && <div className="text-[11px] text-red-600 mt-1">{err}</div>}
      <div className="text-[11px] text-[var(--muted)] mt-1">Standard agenzia: {pctIt(std)}</div>
    </div>
  );
}

function DatiIncarico({ d, draft, onDraft, errs = {} }) {
  // Nessun salvataggio qui: le modifiche finiscono nella bozza della scheda Immobile (barra "Salva").
  const base = formDa(d.incarico);
  const f = draft || base;
  const setF = (nf) => onDraft(nf, base);
  const dis = !d.puo_modificare;
  const att = d.incarico.in_attesa;
  return (
    <div className="card-immo p-5" data-testid="provv-dati-incarico">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <h3 className="text-[13px] font-bold uppercase tracking-wider text-[#FC82ED]">Dati dell'incarico</h3>
        {d.bloccato && <span data-testid="provv-bloccato" className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-600 bg-slate-100 border border-[var(--border)] px-2 py-1 rounded-full"><Lock size={12} /> Proposta accettata: modificano solo i Soci</span>}
      </div>
      {att && (
        <div data-testid="provv-in-attesa" className="mb-4 p-3 rounded-xl border border-[#FFB020]/40 bg-[#FFB020]/10 text-sm text-[#9A6A00] flex items-start gap-2">
          <Clock size={16} className="shrink-0 mt-0.5" />
          <span>Modifica in attesa di approvazione (richiesta da {att.richiesto_da}): venditore {att.provv_venditore_modo === "eur" ? euro(att.provv_venditore_eur) : pctIt(att.provv_venditore_pct)}{att.provv_acquirente_modo ? `, acquirente ${att.provv_acquirente_modo === "eur" ? euro(att.provv_acquirente_eur) : pctIt(att.provv_acquirente_pct)}` : ""}. Finché non viene approvata valgono i dati attuali.</span>
        </div>
      )}
      <div className="grid md:grid-cols-3 gap-5">
        {LATI.map(([l, label]) => <LatoInput key={l} l={l} label={label} f={f} setF={setF} std={d.standard[l]} disabled={dis} err={errs[`provv_${l}_pct`] || errs[`provv_${l}_eur`] || errs[`provv_${l}_modo`]} />)}
        <div>
          <div className="text-[11px] font-bold uppercase tracking-wide text-[var(--muted)] mb-1.5">Minimo garantito</div>
          <div className="flex items-center gap-2 md:mt-[34px]">
            <input data-testid="provv-minimo" type="number" min="0" step="100" disabled={dis} value={f.provv_minimo_eur} onChange={(e) => setF({ ...f, provv_minimo_eur: e.target.value })} className={inp} placeholder="Facoltativo" />
            <span className="text-xs text-[var(--muted)] whitespace-nowrap">€ + IVA</span>
          </div>
          <div className="text-[11px] text-[var(--muted)] mt-1">Si applica alla provvigione del venditore</div>
        </div>
      </div>
    </div>
  );
}

function Calcolo({ d }) {
  const righe = [["prezzo", "Prezzo"], ["venditore", "Provvigione venditore"], ["acquirente", "Provvigione acquirente"], ["imponibile", "Totale imponibile"], ["iva", `IVA ${d.aliquota_iva}%`], ["totale", "Totale con IVA"]];
  return (
    <div className="card-immo p-5 overflow-x-auto" data-testid="provv-calcolo">
      <h3 className="text-[13px] font-bold uppercase tracking-wider text-[#FC82ED] mb-3">Calcolo automatico</h3>
      <table className="w-full text-sm min-w-[520px]">
        <thead><tr className="text-[11px] uppercase tracking-wide text-[var(--muted)]"><th className="text-left py-2 font-semibold"></th>{COLS.map(([k, t]) => <th key={k} className="text-right py-2 font-semibold">{t}</th>)}</tr></thead>
        <tbody>
          {righe.map(([k, t]) => (
            <tr key={k} className={`border-t border-[var(--border)] ${k === "totale" ? "font-bold" : ""}`}>
              <td className="py-2 text-slate-600">{t}</td>
              {COLS.map(([c]) => <td key={c} data-testid={`provv-${c}-${k}`} className="py-2 text-right mono">{d.colonne[c] ? euro(d.colonne[c][k]) : <span className="text-[var(--muted)]">—</span>}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
      {!d.colonne.proposta && <p className="text-[11px] text-[var(--muted)] mt-2">"Sulla proposta" compare quando c'è una proposta; "Sul venduto" con preliminare o rogito.</p>}
    </div>
  );
}

function Ripartizione({ d }) {
  return (
    <div className="card-immo p-5" data-testid="provv-ripartizione">
      <h3 className="text-[13px] font-bold uppercase tracking-wider text-[#FC82ED] mb-1">Ripartizione</h3>
      <p className="text-[11px] text-[var(--muted)] mb-3">Stesse regole di "Provvigioni & Quote". Prima della vendita è un'anteprima sull'importo più aggiornato.</p>
      <div className="grid md:grid-cols-2 gap-4">
        {d.ripartizione.map((r) => (
          <div key={r.lato} data-testid={`provv-rip-${r.lato}`} className="rounded-xl border border-[var(--border)] p-4">
            <div className="flex items-center justify-between mb-2"><span className="text-sm font-semibold capitalize">Lato {r.lato}</span><span className="mono text-sm">{euro(r.imponibile)}</span></div>
            {r.collaboratori.map((c, i) => (
              <div key={i} className="flex justify-between text-sm py-1 border-t border-[var(--border)]"><span className="text-slate-600">{c.nome} · {RUOLI[c.ruolo] || c.ruolo} ({pctIt(c.pct)})</span><span className="mono">{euro(c.importo)}</span></div>
            ))}
            {r.agente_mancante && <div className="text-[11px] text-[var(--muted)] py-1 border-t border-[var(--border)]">Agente dell'acquirente non ancora indicato</div>}
            <div className="flex justify-between text-sm py-1 border-t border-[var(--border)] font-bold"><span>Quota agenzia</span><span data-testid={`provv-rip-${r.lato}-agenzia`} className="mono">{euro(r.agenzia)}</span></div>
            {!r.da_provvigione && r.imponibile > 0 && <div className="text-[10px] uppercase font-bold text-[var(--muted)] mt-1">Anteprima</div>}
          </div>
        ))}
      </div>
    </div>
  );
}

function StatoIncasso({ d, children }) {
  const cur = STATI.findIndex(([k]) => k === d.stato);
  return (
    <div className="card-immo p-5" data-testid="provv-stato">
      <h3 className="text-[13px] font-bold uppercase tracking-wider text-[#FC82ED] mb-3">Stato e incasso</h3>
      <div className="flex items-center gap-1 flex-wrap mb-4">
        {STATI.map(([k, t], i) => (
          <div key={k} className="flex items-center gap-1">
            <span data-testid={`provv-stato-${k}`} className={`px-2.5 py-1 rounded-full text-[11px] font-semibold border ${i === cur ? "bg-[#FC82ED] text-black border-transparent" : i < cur ? "bg-emerald-400/10 text-emerald-600 border-emerald-400/30" : "bg-slate-100 text-slate-400 border-slate-200"}`}>{t}</span>
            {i < STATI.length - 1 && <span className="text-slate-300 text-xs">→</span>}
          </div>
        ))}
      </div>
      {d.mutuo_fino_al && <p data-testid="provv-mutuo" className="text-xs text-[#9A6A00] -mt-2 mb-3">Condizione sospensiva mutuo fino al {fmtDate(d.mutuo_fino_al)}: resta Probabile, dal giorno dopo diventa Sicura in automatico.</p>}
      <div className="grid md:grid-cols-2 gap-4 mb-2">
        {d.incassi.map((x) => (
          <div key={x.lato} data-testid={`provv-incasso-${x.lato}`} className="rounded-xl border border-[var(--border)] p-4 text-sm space-y-1">
            <div className="font-semibold capitalize flex items-center gap-2">Parte {x.lato}{x.stato === "incassata" && <CheckCircle2 size={14} className="text-emerald-500" />}</div>
            <div className="flex justify-between"><span className="text-[var(--muted)]">Importo incassato</span><span className="mono">{x.incassato != null ? euro(x.incassato) : "—"}</span></div>
            <div className="flex justify-between"><span className="text-[var(--muted)]">Data incasso</span><span>{x.data_incasso ? fmtDate(x.data_incasso) : x.data_prevista ? `prevista ${fmtDate(x.data_prevista)}` : "—"}</span></div>
            <div className="flex justify-between gap-2"><span className="text-[var(--muted)]">Estratto conto</span>
              {x.estratto ? (d.puo_vedere_estratto
                ? <a data-testid={`provv-estratto-${x.lato}`} href={`/finanza?mese=${x.estratto.mese}`} className="inline-flex items-center gap-1 text-[#FC82ED] hover:underline text-right"><Landmark size={13} /> {fmtDate(x.estratto.data)} · {euro(x.estratto.importo)}</a>
                : <span className="text-right">{fmtDate(x.estratto.data)} · {euro(x.estratto.importo)}</span>)
                : <span className="text-[var(--muted)]">non abbinata</span>}
            </div>
            {!x.provvigione_id && <div className="text-[11px] text-[var(--muted)]">{x.regola_oro ? "Segnata come incassata al preliminare" : "La provvigione nasce con la vendita (Rogiti)"}</div>}
          </div>
        ))}
      </div>
      {children}
    </div>
  );
}

export default function ProvvigioniTab({ id, children, draft, onDraft, errs }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  const load = () => api.get(`/immobili/${id}/provvigioni`).then((r) => setD(r.data)).catch((e) => setErr(apiError(e)));
  useEffect(() => { load(); }, [id]); // eslint-disable-line
  if (err) return <div className="card-immo p-8 text-center text-sm text-[var(--muted)]" data-testid="provv-errore">{err}</div>;
  if (!d) return <div className="skeleton h-64 rounded-2xl" />;
  return (
    <div className="space-y-4" data-testid="immobile-provvigioni-tab">
      <DatiIncarico d={d} draft={draft} onDraft={onDraft} errs={errs} />
      <Calcolo d={d} />
      <Ripartizione d={d} />
      <StatoIncasso d={d}>{children}</StatoIncasso>
    </div>
  );
}
